/**
 * @fileoverview IronCase application catalog — the stubbed list of "apps"
 * exposed in the AppPortalDialog (Applications launcher). Mirrors Foundry's
 * application-portal grouping: a small set of categories, each holding a
 * handful of typed entries. Items with `status: "coming-soon"` render as
 * disabled cards in the dialog.
 *
 * Add new apps here — the dialog automatically picks them up via
 * `IRONCASE_APP_CATEGORIES`. Each entry carries a `color` used as the app
 * card's icon-tile background (Foundry-style colored tiles).
 */

import type { IconName } from "@blueprintjs/core";

export interface AppCatalogEntry {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly icon: IconName;
  /** Hex color for the card's icon tile background. */
  readonly color: string;
  /** Category label, used to group entries in the navigation rail. */
  readonly category: string;
  /** Route to navigate to when the user clicks the card. */
  readonly href: string;
  /**
   * Whether the app is built.
   *
   * THE ONLY SOURCE OF TRUTH FOR READINESS. "Coming soon" used to also be a
   * category, so an app could be filed by readiness instead of by function —
   * and the two disagreed for 12 of 20 entries: those sat in Investigations,
   * Intelligence, Operations or Admin while carrying status "coming-soon".
   *
   * A "Coming soon" heading holding 4 of 16 unbuilt apps is worse than no
   * heading, because it reads as the complete list of what is missing. Category
   * now answers "what is this for" and status answers "can I use it yet".
   */
  readonly status: "live" | "coming-soon";
}

/** Ordered list of category labels — drives navigation order in the dialog. */
/**
 * The categories the launcher groups by. Two apps, one category — the rail
 * still renders because the launcher groups whatever it is given, and a second
 * category appears when a second kind of app does.
 */
export const IRONCASE_APP_CATEGORIES: readonly string[] = ["Administration"];

/**
 * THE PRODUCT IS TWO APPS.
 *
 * This listed twenty-seven. The rest are in `.archive/routes/` — built, working,
 * and set aside rather than deleted, because a working page removed from the
 * tree is a decision nobody can review later, and because several of them
 * (Cases, Approvals, Entities) are real work that a later scope decision may
 * want back.
 *
 * Both of these have GROUND TRUTH: the live tenant serves
 * `/workspace/control-panel` and `/workspace/compass`, both are captured in
 * foundry-ui, and both have a failing parity gate pointed at them
 * (`pnpm fidelity:control-panel`). That is the reason to keep exactly these two:
 * they are the ones whose "does it match?" question has an answer.
 */
export const IRONCASE_APP_CATALOG: readonly AppCatalogEntry[] = [
  {
    id: "control-panel-app",
    name: "Control Panel",
    description: "Organization settings, roles, permissions and enrollment.",
    icon: "cog",
    color: "#495057",
    category: "Administration",
    href: "/workspace/control-panel",
    status: "live",
  },
  {
    /*
      Compass upstream, where it is called "Projects & files". Kept as "Files"
      because that is this product's word for it — the UI language is ours even
      where the implementation is theirs.
    */
    id: "compass",
    name: "Files",
    description: "Browse case files and evidence across the agency.",
    icon: "folder-close",
    color: "#0c8599",
    category: "Administration",
    href: "/workspace/compass",
    status: "live",
  },
];
